import { strFromU8, strToU8, unzipSync, zipSync } from 'fflate';
import { SHA256_RE, sha256Hex } from '../lib/hash.ts';
import { uuidv7 } from '../lib/uuid.ts';
import { type BlobStore, blobKey, blobUri, shaOfUri } from './blobStore.ts';
import { type ProjectFile, readProjectJson, referencedBlobs } from './record.ts';

/**
 * `.betterlights` backup files: a zip with
 *   manifest.json           { format: 'better-lights-backup', version: 1, createdAt, projects: [ids] }
 *   projects/<id>.json      the project document (schemaVersion 1)
 *   blobs/<sha256>          every referenced file, content-addressed
 * The same file moves projects between browsers, devices and (later) the native apps.
 */
export const BACKUP_EXTENSION = '.betterlights';
export const BACKUP_MIME = 'application/vnd.better-lights+zip';
const FORMAT = 'better-lights-backup';

export async function exportBackup(projects: ProjectFile[], blobs: BlobStore, now = Date.now()): Promise<Blob> {
  const files: Record<string, Uint8Array> = {};
  files['manifest.json'] = strToU8(
    JSON.stringify({ format: FORMAT, version: 1, createdAt: now, projects: projects.map((p) => p.id) }, null, 2),
  );
  for (const p of projects) {
    files[`projects/${p.id}.json`] = strToU8(JSON.stringify(p, null, 2));
    for (const sha of referencedBlobs(p)) {
      if (files[blobKey(sha)]) continue;
      const blob = await blobs.get(sha);
      // Images are already compressed: store them without deflate.
      if (blob) files[blobKey(sha)] = new Uint8Array(await blob.arrayBuffer());
    }
  }
  const zipped = zipSync(
    Object.fromEntries(
      Object.entries(files).map(([name, data]) => [name, [data, { level: name.endsWith('.json') ? 6 : 0 }]]),
    ) as Parameters<typeof zipSync>[0],
  );
  return new Blob([zipped as Uint8Array<ArrayBuffer>], { type: BACKUP_MIME });
}

export interface ImportResult {
  projects: ProjectFile[];
  skippedFiles: number;
}

/**
 * Reads a backup (or a bare project .json, including legacy records with data: URIs).
 * Blobs are verified against their hash. Projects whose id already exists get a new id
 * (keep both). Returns the projects to save; blobs are already stored.
 */
export async function importBackup(file: Blob, blobs: BlobStore, existingIds: Set<string>): Promise<ImportResult> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  const isZip = bytes[0] === 0x50 && bytes[1] === 0x4b;
  const out: ProjectFile[] = [];
  let skippedFiles = 0;
  if (!isZip) {
    const project = readProjectJson(JSON.parse(strFromU8(bytes)));
    out.push(await inlineDataUris(project, blobs));
  } else {
    const entries = unzipSync(bytes);
    const manifest = entries['manifest.json'] ? JSON.parse(strFromU8(entries['manifest.json'])) : null;
    if (manifest && manifest.format !== FORMAT) throw new Error('This is not a Better Lights backup.');
    for (const [name, data] of Object.entries(entries)) {
      const sha = name.startsWith('blobs/') ? name.slice(6) : null;
      if (sha && SHA256_RE.test(sha)) await blobs.putVerified(sha, new Blob([data as Uint8Array<ArrayBuffer>]));
    }
    for (const [name, data] of Object.entries(entries)) {
      if (!/^projects\/[^/]+\.json$/.test(name)) continue;
      const project = readProjectJson(JSON.parse(strFromU8(data)));
      const missing = [];
      for (const sha of referencedBlobs(project)) if (!(await blobs.has(sha))) missing.push(sha);
      if (project.photo && shaOfUri(project.photo.uri) && missing.includes(shaOfUri(project.photo.uri)!)) {
        skippedFiles += 1;
        continue;
      }
      out.push(dropMissing(project, new Set(missing)));
    }
  }
  if (out.length === 0) throw new Error('No projects found in that file.');
  for (const p of out) {
    if (existingIds.has(p.id)) p.id = uuidv7();
    existingIds.add(p.id);
  }
  return { projects: out, skippedFiles };
}

function dropMissing(p: ProjectFile, missing: Set<string>): ProjectFile {
  if (missing.size === 0) return p;
  const ok = (uri: string) => !missing.has(shaOfUri(uri) ?? '');
  return { ...p, aiResults: p.aiResults.filter((a) => ok(a.uri)), exports: p.exports.filter((e) => ok(e.uri)) };
}

/** Legacy web records keep images as data: URIs: move them into the blob store. */
async function inlineDataUris(p: ProjectFile, blobs: BlobStore): Promise<ProjectFile> {
  const store = async (uri: string): Promise<string> => {
    if (!uri.startsWith('data:')) {
      if (!shaOfUri(uri)) throw new Error('This project refers to files that are not in it.');
      return uri;
    }
    const blob = await (await fetch(uri)).blob();
    return blobUri(await blobs.put(blob));
  };
  const photoUri = await store(p.photo.uri);
  const photoBlob = await blobs.getByUri(photoUri);
  return {
    ...p,
    photo: { ...p.photo, uri: photoUri, sha256: shaOfUri(photoUri) ?? undefined, bytes: photoBlob?.size },
    aiResults: await Promise.all(p.aiResults.map(async (a) => ({ ...a, uri: await store(a.uri) }))),
    exports: await Promise.all(p.exports.map(async (e) => ({ ...e, uri: await store(e.uri) }))),
  };
}

export async function verifyBlob(sha: string, data: Blob): Promise<boolean> {
  return (await sha256Hex(data)) === sha;
}
